/* Danh sách khách quá thời gian — Người vận hành chọn cách chia (tự động / tuần tự / 1 người) rồi chia lại */
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Segmented, Select, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowRight, Bot, ListOrdered, Settings2, Shuffle, User } from 'lucide-react';
import { idleText, type Staff, type StaleLead } from './reassign';

export interface ReassignPick { lead: StaleLead; to: Staff }
type Mode = 'auto' | 'seq' | 'one';
type Targets = Record<number, number | undefined>;

export default function ReassignModal({ open, kind, rule, items, staffs, loads, suggest, onClose, onConfirm, onSettings }: {
  open: boolean;
  kind: 'call' | 'chat';
  /** "quá 30 phút không tương tác" */
  rule: string;
  items: StaleLead[];
  staffs: Staff[];
  /** Số khách mỗi người đang giữ — hiện cạnh tên để chọn cho đều */
  loads: Record<number, number>;
  /** Người nhận gợi ý theo cài đặt (ít khách nhất / xoay vòng) */
  suggest: Record<number, Staff>;
  onClose: () => void;
  onConfirm: (picks: ReassignPick[]) => void;
  /** Mở cài đặt chia lại ngay tại màn chia (họp 30/10: chỉnh xong quay lại chia luôn) */
  onSettings?: () => void;
}) {
  const who = kind === 'call' ? 'Telesales' : 'Trực page';
  const [picked, setPicked] = useState<React.Key[]>([]);
  const [target, setTarget] = useState<Targets>({});
  const [mode, setMode] = useState<Mode>('auto');
  const [one, setOne] = useState<number>();
  const [seq, setSeq] = useState<number[]>([]);

  /**
   * Tính người nhận cho các khách đang tick theo cách chia.
   * Không giao lại cho chính người đang giữ khách: Một người → giữ gợi ý; Tuần tự → bỏ qua sang người kế tiếp.
   */
  const compute = (m: Mode, keys: React.Key[], base: Targets, oneId?: number, seqIds: number[] = []): Targets => {
    const next = { ...base };
    const rows = items.filter((x) => keys.includes(x.r.id));
    if (m === 'auto') rows.forEach((x) => { next[x.r.id] = suggest[x.r.id]?.id; });
    if (m === 'one' && oneId) rows.forEach((x) => { next[x.r.id] = x.info.staff?.id !== oneId ? oneId : suggest[x.r.id]?.id; });
    if (m === 'seq' && seqIds.length) {
      let i = 0;
      rows.forEach((x) => {
        for (let k = 0; k < seqIds.length; k += 1) {
          const id = seqIds[(i + k) % seqIds.length];
          if (id !== x.info.staff?.id) { next[x.r.id] = id; i = i + k + 1; return; }
        }
        next[x.r.id] = suggest[x.r.id]?.id;
      });
    }
    return next;
  };

  const reset = () => {
    const keys = items.filter((x) => !x.blocked).map((x) => x.r.id);
    setPicked(keys);
    setMode('auto');
    setOne(undefined);
    setSeq([]);
    setTarget(compute('auto', items.map((x) => x.r.id), {}));
  };
  useEffect(() => { if (open) reset(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeMode = (m: Mode) => { setMode(m); setTarget((t) => compute(m, picked, t, one, seq)); };
  const chooseOne = (v?: number) => { setOne(v); setTarget((t) => compute('one', picked, t, v)); };
  const chooseSeq = (v: number[]) => { setSeq(v); setTarget((t) => compute('seq', picked, t, undefined, v)); };
  // Tick thêm khách: Tuần tự tính lại cả vòng cho đều; cách khác chỉ điền cho khách mới tick
  const onPick = (keys: React.Key[]) => {
    const added = keys.filter((k) => !picked.includes(k));
    setTarget((t) => (mode === 'seq' ? compute('seq', keys, t, undefined, seq) : compute(mode, added, t, one, seq)));
    setPicked(keys);
  };

  const options = (exclude?: number) => staffs
    .filter((s) => s.id !== exclude)
    .map((s) => ({ value: s.id, label: s.name, load: loads[s.id] ?? 0 }));
  // Trong danh sách thả xuống hiện thêm số khách đang giữ; ô đã chọn chỉ hiện tên
  const optionRender = (o: { data: { label: string; load: number } }) => (
    <span className="flex items-center justify-between gap-2"><span className="truncate">{o.data.label}</span><span className="shrink-0 text-[12px] text-gray-400">giữ {o.data.load}</span></span>
  );
  const chosen = items.filter((x) => picked.includes(x.r.id));
  const missing = chosen.filter((x) => !target[x.r.id]).length;
  const confirm = () => onConfirm(chosen.flatMap((x) => {
    const to = staffs.find((s) => s.id === target[x.r.id]);
    return to ? [{ lead: x, to }] : [];
  }));

  // Đếm khách mỗi người sẽ nhận thêm để thấy có dồn vào một người không
  const incoming = useMemo(() => {
    const out: Record<number, number> = {};
    chosen.forEach((x) => { const id = target[x.r.id]; if (id) out[id] = (out[id] ?? 0) + 1; });
    return out;
  }, [chosen, target]);

  const columns: ColumnsType<StaleLead> = [
    {
      title: 'KHÁCH',
      width: 210,
      render: (_, x) => (
        <div className="leading-tight">
          <div className="font-semibold text-gray-800 dark:text-gray-100">{x.r.display_name || x.r.fullname || `KH-${x.r.id}`}</div>
          <div className="text-[12px] text-gray-400">{x.r.customer_code || `KH-${x.r.id}`}{x.r.customer_tag?.name ? ` · ${x.r.customer_tag.name.split(/\s*-\s*/)[0]}` : ''}</div>
        </div>
      ),
    },
    {
      title: kind === 'call' ? 'CHƯA TƯƠNG TÁC' : 'KHÁCH CHỜ TRẢ LỜI',
      width: 150,
      render: (_, x) => (
        <div className="leading-tight">
          <div className="font-semibold text-red-600">{idleText(x.info.idle)}</div>
          {x.blocked && <Tag color="orange" className="m-0 mt-1">Đã chia tối đa</Tag>}
        </div>
      ),
    },
    {
      title: 'ĐANG GIỮ',
      width: 160,
      render: (_, x) => (x.info.staff ? <span className="text-gray-700 dark:text-gray-300">{x.info.staff.name}</span> : <span className="text-orange-500">Chưa phân</span>),
    },
    {
      title: `GIAO CHO ${who.toUpperCase()}`,
      render: (_, x) => (
        <div>
          <div className="flex items-center gap-2">
            <ArrowRight size={14} className="shrink-0 text-gray-400" />
            <Select
              size="small"
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder={`Chọn ${who}`}
              value={target[x.r.id]}
              onChange={(v) => { setTarget((t) => ({ ...t, [x.r.id]: v })); if (v && !picked.includes(x.r.id)) setPicked((p) => [...p, x.r.id]); }}
              options={options(x.info.staff?.id)}
              optionRender={optionRender}
              status={picked.includes(x.r.id) && !target[x.r.id] ? 'error' : undefined}
              className="min-w-0 flex-1"
            />
            {suggest[x.r.id] && target[x.r.id] === suggest[x.r.id].id && mode !== 'auto' && <Tooltip title="Gợi ý theo cài đặt"><Tag className="m-0" color="cyan">Gợi ý</Tag></Tooltip>}
          </div>
          {mode === 'one' && one && x.info.staff?.id === one && picked.includes(x.r.id) && (
            <div className="mt-1 pl-[22px] text-[11.5px] text-amber-600">{x.info.staff.name} đang giữ khách này nên không giao lại cho chính họ — đã dùng gợi ý</div>
          )}
        </div>
      ),
    },
  ];

  const MODES = [
    { value: 'auto', label: <span className="flex items-center gap-1.5 px-1"><Bot size={14} />Tự động</span> },
    { value: 'seq', label: <span className="flex items-center gap-1.5 px-1"><ListOrdered size={14} />Tuần tự</span> },
    { value: 'one', label: <span className="flex items-center gap-1.5 px-1"><User size={14} />Tất cả cho 1 người</span> },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={980}
      title={(
        <span className="flex items-center gap-2 pr-8">
          <Shuffle size={18} className="text-[#3e91ab]" />Chia lại khách {rule} · {who}
          {onSettings && <Tooltip title="Cài đặt chia lại, nhóm dịch vụ"><Button size="small" type="text" icon={<Settings2 size={15} />} onClick={onSettings} aria-label="Cài đặt chia lại" /></Tooltip>}
        </span>
      )}
      footer={(
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-[12.5px] text-gray-500">
            Đã chọn <b>{chosen.length}</b>/{items.length} khách{missing ? <span className="text-red-500"> · {missing} khách chưa có người nhận</span> : null}
          </span>
          <div className="flex gap-2">
            <Button onClick={onClose}>Huỷ</Button>
            <Button type="primary" disabled={!chosen.length || missing > 0} onClick={confirm} icon={mode === 'auto' ? <Bot size={14} /> : <Shuffle size={14} />} className="!bg-[#3e91ab]">
              {mode === 'auto' ? 'Chia tự động' : 'Chia lại'} {chosen.length} khách
            </Button>
          </div>
        </div>
      )}
    >
      <div className="space-y-3">
        <div className="space-y-2.5 rounded-lg bg-gray-50 p-3 text-[13px] dark:bg-gray-900/40">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-medium text-gray-700 dark:text-gray-200">Cách chia</span>
            <Segmented value={mode} onChange={(v) => changeMode(v as Mode)} options={MODES} />
          </div>
          {mode === 'auto' && (
            <div className="text-[12.5px] text-gray-500">Hệ thống tự chọn người nhận theo Cài đặt chia lại (người đang giữ ít khách nhất / xoay vòng). Vẫn sửa được từng khách bên dưới.</div>
          )}
          {mode === 'seq' && (
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-gray-600 dark:text-gray-300">Chia lần lượt cho</span>
                <Select
                  mode="multiple"
                  size="small"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder={`Chọn các ${who} theo thứ tự`}
                  value={seq}
                  onChange={chooseSeq}
                  options={options()}
                  optionRender={optionRender}
                  className="min-w-[320px] flex-1"
                />
              </div>
              <div className="text-[12px] text-gray-400">Khách thứ 1 → người thứ 1, khách thứ 2 → người thứ 2… hết danh sách thì quay lại người đầu. Bỏ qua người đang giữ chính khách đó.</div>
            </div>
          )}
          {mode === 'one' && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-gray-600 dark:text-gray-300">Giao tất cả khách đang tick cho</span>
              <Select size="small" showSearch allowClear optionFilterProp="label" placeholder={`Chọn ${who}`} value={one} onChange={chooseOne} options={options()} optionRender={optionRender} className="w-[260px]" />
            </div>
          )}
        </div>

        {Object.keys(incoming).length > 0 && (
          <div className="flex flex-wrap gap-1.5 text-[12px]">
            <span className="text-gray-500">Sẽ nhận thêm:</span>
            {Object.entries(incoming).map(([id, n]) => (
              <Tag key={id} className="m-0">{staffs.find((s) => s.id === Number(id))?.name} +{n}</Tag>
            ))}
          </div>
        )}

        <Table
          rowKey={(x) => x.r.id}
          size="small"
          columns={columns}
          dataSource={items}
          pagination={false}
          scroll={{ y: 420 }}
          rowSelection={{ selectedRowKeys: picked, onChange: onPick }}
        />
        <div className="text-[12px] text-gray-400">
          Không giao lại cho chính người đang giữ khách. Khách “Đã chia tối đa” không được chọn sẵn — Người vận hành vẫn có thể tick để giao tay.
        </div>
      </div>
    </Modal>
  );
}
